import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import CookieJs from "js-cookie";
import get from "lodash/get";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import branch from "../../services/branch";
import plService from "../../services/pl";
import queryDefault from "../../utils/queryDefault";

const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const numericInputFields = [
  { name: "sales", label: "Sales" },
  { name: "gaji", label: "Gaji" },
  { name: "kertas", label: "Kertas" },
  { name: "internet", label: "Internet" },
  { name: "listrik", label: "Listrik" },
  { name: "pengiriman", label: "Pengiriman" },
  { name: "perlengkapan", label: "Perlengkapan" },
  { name: "maintenance", label: "Maintenance" },
  { name: "akomodasi_lainnya", label: "Akomodasi Lainnya" },
  { name: "transport", label: "Transport" },
  { name: "kol", label: "KOL" },
  { name: "bagi_hasil", label: "Bagi Hasil" },
  { name: "renov_amortisasi", label: "Renov Amortisasi" },
  { name: "sewa", label: "Sewa" },
  { name: "sewa_amortisasi", label: "Sewa Amortisasi" },
  { name: "service_charge", label: "Service Charge" },
] as const;

export default function InputPL() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notification = useNotification();
  const user = JSON.parse(CookieJs.get("USER") || "{}");

  const form = useForm({
    defaultValues: {
      branch_id: 0,
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      sales: 0,
      gaji: 0,
      kertas: 0,
      internet: 0,
      listrik: 0,
      pengiriman: 0,
      perlengkapan: 0,
      maintenance: 0,
      akomodasi_lainnya: 0,
      transport: 0,
      kol: 0,
      bagi_hasil: 0,
      renov_amortisasi: 0,
      sewa: 0,
      sewa_amortisasi: 0,
      service_charge: 0,
    },
  });

  const allYears = useMemo(() => {
    const years: number[] = [];
    const latestYear = new Date().getFullYear();
    for (let year = latestYear; year >= 2020; year--) {
      years.push(year);
    }
    return years;
  }, []);

  const getBranches = useQuery(
    ["branch"],
    () => {
      return branch.getAllBranch({ keyword: "" });
    },
    {
      ...queryDefault,
    }
  );

  useEffect(() => {
    if (!id) {
      return;
    }

    plService.getPL(id).then((response) => {
      const data = get(response, "data", {});
      form.reset({
        branch_id: Number(get(data, "branch_id", 0)),
        month: Number(get(data, "month", 0)),
        year: Number(get(data, "year", 0)),
        sales: Number(get(data, "sales", 0)),
        gaji: Number(get(data, "gaji", 0)),
        kertas: Number(get(data, "kertas", 0)),
        internet: Number(get(data, "internet", 0)),
        listrik: Number(get(data, "listrik", 0)),
        pengiriman: Number(get(data, "pengiriman", 0)),
        perlengkapan: Number(get(data, "perlengkapan", 0)),
        maintenance: Number(get(data, "maintenance", 0)),
        akomodasi_lainnya: Number(get(data, "akomodasi_lainnya", 0)),
        transport: Number(get(data, "transport", 0)),
        kol: Number(get(data, "kol", 0)),
        bagi_hasil: Number(get(data, "bagi_hasil", 0)),
        renov_amortisasi: Number(get(data, "renov_amortisasi", 0)),
        sewa: Number(get(data, "sewa", 0)),
        sewa_amortisasi: Number(get(data, "sewa_amortisasi", 0)),
        service_charge: Number(get(data, "service_charge", 0)),
      });
    });
  }, [id, form]);

  const createPL = useMutation("create-pl", plService.createPL, {
    onSuccess: (response) => {
      notification.onOpen({
        message: response?.message || "P&L was successfully created!",
        type: "success",
        position: "top",
      });
      navigate("/app/pl");
    },
    onError: (error: any) => {
      notification.onOpen({
        message: error?.response?.data?.message || "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const editPL = useMutation(
    "edit-pl",
    ({ payloadId, payload }: { payloadId: string; payload: any }) =>
      plService.updatePL(payloadId, payload),
    {
      onSuccess: (response) => {
        notification.onOpen({
          message: response?.message || "P&L was successfully updated!",
          type: "success",
          position: "top",
        });
        navigate("/app/pl");
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const onSubmit = (values: any) => {
    const payload = {
      data: {
        ...values,
        username: user?.username || user?.name || "",
      },
    };

    if (id) {
      editPL.mutate({ payloadId: id, payload });
      return;
    }

    createPL.mutate(payload);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label={id ? "Edit P&L" : "Create P&L"}
        breadcrumbs={[{ label: "P&L", href: "/app/pl" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 11]}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <FormLabel required>Branch</FormLabel>
                <Controller
                  name="branch_id"
                  control={form.control}
                  render={() => (
                    <Select
                      value={form.getValues().branch_id}
                      onChange={(event) => {
                        form.setValue("branch_id", Number(event.target.value));
                      }}
                    >
                      <MenuItem value={0}>Select Branch</MenuItem>
                      {get(getBranches, "data.data", []).map((item: any) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <FormLabel required>Month</FormLabel>
                <Controller
                  name="month"
                  control={form.control}
                  render={() => (
                    <Select
                      value={form.getValues().month}
                      onChange={(event) => {
                        form.setValue("month", Number(event.target.value));
                      }}
                    >
                      {monthOptions.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <FormLabel required>Year</FormLabel>
                <Controller
                  name="year"
                  control={form.control}
                  render={() => (
                    <Select
                      value={form.getValues().year}
                      onChange={(event) => {
                        form.setValue("year", Number(event.target.value));
                      }}
                    >
                      {allYears.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            {numericInputFields.map((field) => (
              <Grid item xs={12} md={4} key={field.name}>
                <FormControl fullWidth margin="normal">
                  <FormLabel>{field.label}</FormLabel>
                  <Controller
                    name={field.name}
                    control={form.control}
                    render={({ field: { ref, ...controllerField } }) => (
                      <TextField
                        {...controllerField}
                        type="number"
                        size="small"
                        inputRef={ref}
                        inputProps={{
                          step: "any",
                        }}
                        onChange={(event) => {
                          const value = event.target.value;
                          form.setValue(
                            field.name,
                            value === "" ? 0 : Number(value)
                          );
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={createPL.isLoading || editPL.isLoading}
            >
              Save
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
